import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button, Calendar } from "../ui";
import { CalendarIcon, CircleX } from "lucide-react";
import { useState } from "react";
import moment from "moment";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DatePicker = ({
  date,
  setDate,
  disabled,
  maxDate,
  minDate,
  onClear,
}: any) => {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(new Date());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          className={cn(
            "w-full items-center justify-between text-left font-normal h-[40px] text-sidebar-foreground group relative",
            !date && "text-muted-foreground"
          )}
          disabled={disabled}
        >
          <span className="max-w-[80%] line-clamp-1">
            {date ? format(new Date(date), "PP") : disabled ? <></> : <span>Please Select</span>}
          </span>
          {disabled ? (
            <></>
          ) : date ? (
            <div
              className="h-5 w-5 cursor-pointer flex items-center justify-end"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClear && onClear();
                setDate("");
              }}
            >
              <CircleX className="stroke-gray-500 group-hover:stroke-black" />
            </div>
          ) : (
            <CalendarIcon className="mr-2 h-4 w-4" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        {/* Month + Year Selectors */}
        <div className="flex items-center justify-between gap-2 px-2 py-2">
          <Select
            value={String(month.getMonth())}
            onValueChange={(val) => {
              const newMonth = new Date(month);
              newMonth.setMonth(Number(val));
              setMonth(newMonth);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {months.map((m, idx) => (
                <SelectItem key={m} value={String(idx)}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={String(month.getFullYear())}
            onValueChange={(val) => {
              const newYear = new Date(month);
              newYear.setFullYear(Number(val));
              setMonth(newYear);
            }}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 100 }, (_, i) => {
                const year = new Date().getFullYear() - 60 + i;
                return (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <Calendar
          mode="single"
          month={month}
          onMonthChange={setMonth}
          selected={date ? new Date(date) : undefined}
          onSelect={(selectedDate) => {
            if (selectedDate) {
              const _selectedDate = moment(selectedDate).format(
                "YYYY-MM-DDTHH:mm:ss"
              );

              if (
                minDate &&
                maxDate &&
                moment(_selectedDate).isSameOrAfter(moment(minDate)) &&
                moment(_selectedDate).isSameOrBefore(moment(maxDate))
              ) {
                setDate(_selectedDate);
              } else if (
                _selectedDate &&
                minDate &&
                moment(_selectedDate).isSameOrAfter(moment(minDate))
              ) {
                setDate(_selectedDate);
              } else if (
                _selectedDate &&
                maxDate &&
                moment(_selectedDate).isSameOrBefore(moment(maxDate))
              ) {
                setDate(_selectedDate);
              } else {
                setDate(_selectedDate);
              }
              setOpen(false);
            }
          }}
          disabled={(day) =>
            (minDate &&
              day.getTime() < new Date(minDate).setHours(0, 0, 0, 0)) ||
            (maxDate &&
              day.getTime() > new Date(maxDate).setHours(23, 59, 59, 999))
          }
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
